import CloseIcon from '@mui/icons-material/Close';
import { Button, Dialog, DialogProps, Typography } from '@mui/material';
import { useFormik } from 'formik';
import React, { memo, useMemo, useState } from 'react';

import { Autocomplete, AutocompleteOption } from '~/components/Autocomplete';
import { useGetDraftCompaniesQuery } from '~/services/rtkQuery/company';
import { SelectCompanyFormValues } from '~/types/formValues';
import { selectCompanyFormSchema } from '~/utils/formSchemas';

import styles from './styles.module.scss';

const PAGE_NUM = 1;
const FETCH_LIMIT = 20;
const MIN_SEARCH_LENGTH = 3;

interface Props extends Omit<DialogProps, 'onClose'> {
  onClose: () => void;
  onCompanySelect: (companyId: number) => void;
}

const Component: React.FC<Props> = ({ open, onClose, onCompanySelect, ...props }) => {
  const [companySearchValue, setCompanySearchValue] = useState('');
  const getDraftCompaniesQuery = useGetDraftCompaniesQuery(
    {
      pageNum: PAGE_NUM,
      perPage: FETCH_LIMIT,
      name: companySearchValue,
    },
    { skip: companySearchValue.trim().length < MIN_SEARCH_LENGTH },
  );
  const formik = useFormik<SelectCompanyFormValues>({
    initialValues: {
      company: null,
    },
    validationSchema: selectCompanyFormSchema,
    onSubmit: values => {
      if (values.company?.value) {
        onCompanySelect(values.company.value as number);

        onClose();
      }
    },
  });

  const companyOptions = useMemo<AutocompleteOption[]>(() => {
    return (
      getDraftCompaniesQuery.data?.result.data.map(company => ({
        label: company.name,
        value: company.id,
      })) || []
    );
  }, [getDraftCompaniesQuery.data]);

  return (
    <Dialog open={open} onClose={onClose} {...props}>
      <form className={styles.modal} onSubmit={formik.handleSubmit}>
        <button aria-label="close" className={styles.modal__close} type="button" onClick={onClose}>
          <CloseIcon sx={{ width: '18px', height: '18px', color: 'var(--gray-2-color)' }} />
        </button>
        <Typography color="text.primary" mb="45px" variant="h5">
          Select a company to generate a link
        </Typography>
        <Autocomplete
          error={formik.touched.company && !!formik.errors.company}
          helperText={formik.touched.company ? formik.errors.company : undefined}
          loading={getDraftCompaniesQuery.isFetching}
          noOptionsText={
            companySearchValue.length >= MIN_SEARCH_LENGTH ? undefined : 'Type to search...'
          }
          options={companyOptions}
          placeholder="Company*"
          value={formik.values.company}
          onBlur={() => formik.setFieldTouched('company')}
          onChange={value => formik.setFieldValue('company', value)}
          onSearchValueChange={setCompanySearchValue}
        />
        <div className={styles.modal__buttons}>
          <Button color="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit">Select</Button>
        </div>
      </form>
    </Dialog>
  );
};

export const SelectCompanyModal = memo(Component);
